import React, { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  ListSubheader,
} from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import CloseIcon from "@material-ui/icons/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUploadAlt,
  faFileDownload,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { getSubDomain } from "../../../../Pages/Common/User Management/HelperFunctions";
import useStyles from "./M_AddTaskForm.styles";
import {
  TASK_API_BASE,
  NORMAL_STATUSES,
  APPROVAL_STATUSES,
  INITIAL_STATE,
  formatFileSize,
  ALLOWED_FILE_EXTENSIONS,
  MAX_FILE_SIZE_BYTES,
} from "./M_AddTaskForm.helper";
import { getFileIcon, getInitials } from "../TasksTable/TasksTable.helper";
import { getBulkDocuments } from "../../../Announcment/Helper/Announcement.helper";
import { containerType } from "../../../../../../Utils/config/config";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import { httpGet, httpPost, httpPut } from "../../../../../../Utils/apis/apis";
import * as toastActions from "../../../../../../store/actions/toastActions";
import * as currentPageActions from "../../../../../../store/actions/currentPageActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";
import { executeMultiSp } from "../../../../Organisms/Common/DealPipelineMovement/DealPipelineMovement.utils";
import { getCRMContactInfo } from "../../../../../../Utils/Helpers/common";
import A_SecuredAction from "../../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../../Utils/Helpers/pageSecurity";

const M_AddTaskForm = (props) => {
  const {
    onClose,
    onTaskCreated,
    loanId: propLoanId,
    setToast,
    editTask,
  } = props;

  const isEditMode = Boolean(editTask);
  const classes = useStyles();
  const { crmId } = useParams();
  const loanId = propLoanId || (isEditMode ? editTask.loanId : "") || crmId || "";

  const { crmContactUserEmail } = getCRMContactInfo();
  const user = useSelector((state) => state.oidc.user);
  const userRoles = useSelector((state) => state.userRoles?.data || {});
  const loggedInUserEmail = useSelector((state) =>
    state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      ? state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      : crmContactUserEmail || user?.profile?.email
  );

  const isLoggedInUser = (email) =>
    Boolean(email) &&
    email.trim().toLowerCase() === (loggedInUserEmail || "").trim().toLowerCase();

  const isRequestor = isEditMode && (editTask.createdBy || "").toLowerCase() === (loggedInUserEmail || "").toLowerCase();
  const isApprover = isEditMode && (editTask.approver || "").toLowerCase() === (loggedInUserEmail || "").toLowerCase();
  const isAssignedTo =
    isEditMode &&
    (editTask.assignedTo || "")
      .split(/[;,]/)
      .map((e) => e.trim().toLowerCase())
      .includes((loggedInUserEmail || "").toLowerCase());
  const isTaskClosed =
    isEditMode &&
    (editTask.status === "Closed" ||
      editTask.status === "Approved" ||
      editTask.status === "Rejected");
  const isTaskAdmin = Boolean(userRoles["HTB-DF-TASK-ADMIN"]);

  const existingFiles = useMemo(() => {
    if (!editTask?.attachments) return [];
    return typeof editTask.attachments === "string"
      ? editTask.attachments
          .split(/[;,]/)
          .map((file) => file.trim())
          .filter(Boolean)
      : [];
  }, [editTask]);

  const [formData, setFormData] = useState(() => {
    if (isEditMode) {
      return {
        taskType: editTask.taskType || "Normal",
        title: editTask.title || "",
        description: editTask.description || "",
        status: editTask.status || "Open",
        assignedTo: editTask.assignedTo || "",
        approver: editTask.approver || "",
        dueDate: editTask.dueDate ? editTask.dueDate.split("T")[0] : "",
        savedStage: editTask.stage || "",
        isMandatory: Boolean(editTask.stage) && Boolean(editTask.isMandatory),
        referenceTaskId: editTask.referenceTaskId || editTask.reference?.id || "",
        notes: editTask.notes || "",
        isNotificationRequired: Boolean(editTask.isNotificationRequired),
      };
    }
    return INITIAL_STATE;
  });

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [removedFiles, setRemovedFiles] = useState([]);

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactPhotos, setContactPhotos] = useState({});

  const [referenceTasks, setReferenceTasks] = useState([]);
  const [refTasksLoading, setRefTasksLoading] = useState(false);

  const [stageGroups, setStageGroups] = useState({});
  const [stagesLoading, setStagesLoading] = useState(false);

  useEffect(() => {
    setMounted(true);

    const fetchContactPhotos = async (contactList) => {
      const emails = new Set();
      contactList.forEach((c) => {
        if (c.Email) emails.add(c.Email.trim().toLowerCase());
      });

      if (emails.size === 0) return;

      try {
        const payload = [
          {
            items: Array.from(emails).map((email) => `profile_${email}`),
            type: containerType.IMAGE,
          },
        ];
        const response = await getBulkDocuments(payload);
        const photosMap = {};
        if (response && response.image) {
          response.image.forEach((img) => {
            const filename = img.file;
            if (filename) {
              const parts = filename.split("/");
              const actualName = parts[parts.length - 1];
              if (actualName.startsWith("profile_")) {
                const email = actualName.replace("profile_", "").toLowerCase();
                photosMap[email] = img.link;
              }
            }
          });
        }
        setContactPhotos((prev) => ({ ...prev, ...photosMap }));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching contact photos:", err);
      }
    };

    const fetchContacts = async () => {
      setContactsLoading(true);
      try {
        const response = await executeMultiSp([
          { id: "Contacts", spName: "DF.usp_GetAllContacts", parameters: {} },
        ]);
        if (response?.Contacts) {
          setContacts(response.Contacts);
          fetchContactPhotos(response.Contacts);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load contacts:", err);
      } finally {
        setContactsLoading(false);
      }
    };

    const fetchReferenceTasks = async () => {
      if (!loanId) return;
      setRefTasksLoading(true);
      try {
        const response = await httpGet(
          `${TASK_API_BASE}/optionsets?loanId=${loanId}`
        );
        if (Array.isArray(response)) {
          const filtered = isEditMode
            ? response.filter((task) => task.id !== editTask.id)
            : response;
          setReferenceTasks(filtered);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load reference tasks:", err);
      } finally {
        setRefTasksLoading(false);
      }
    };

    const fetchStages = async () => {
      setStagesLoading(true);
      try {
        const response = await httpGet(`${TASK_API_BASE}/stages`);
        if (response && typeof response === "object" && !Array.isArray(response)) {
          setStageGroups(response);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load task stages:", err);
      } finally {
        setStagesLoading(false);
      }
    };

    fetchContacts();
    fetchReferenceTasks();
    fetchStages();
  }, [loanId, isEditMode, editTask?.id]);

  const availableContacts = useMemo(
    () =>
      contacts.filter(
        (contact) =>
          contact.Name &&
          contact.Email &&
          contact.Name.trim() !== "" &&
          contact.Email.trim() !== ""
      ),
    [contacts]
  );

  const isApprovalTask = formData.taskType === "ApprovalBased";
  const hasSelectedStage = Boolean(formData.savedStage);

  const statusOptions = useMemo(() => {
    let options = isApprovalTask ? APPROVAL_STATUSES : NORMAL_STATUSES;

    if (isApprovalTask) {
      if (!isApprover || isRequestor) {
        options = options.filter(
          (option) => option.value !== "Approved" && option.value !== "Rejected"
        );
      }
    } else if (isEditMode && !isAssignedTo) {
      options = options.filter((option) => option.value !== "Closed");
    }

    if (formData.status && !options.some((o) => o.value === formData.status)) {
      const allOptions = [...NORMAL_STATUSES, ...APPROVAL_STATUSES];
      const current = allOptions.find((o) => o.value === formData.status);
      if (current) options = [...options, current];
    }

    return options;
  }, [isApprovalTask, isRequestor, isApprover, isAssignedTo, isEditMode, formData.status]);

  const visibleExistingFiles = existingFiles.filter(
    (file) => !removedFiles.includes(file)
  );

  const clearError = (fieldName) => {
    setErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleDownload = async (fileKey) => {
    if (!fileKey) return;
    try {
      const subdomain = getSubDomain();
      const url = `${process.env.DOCUMENT_HELPER}`;
      const headers = {
        filename: fileKey,
        type: "task",
        "file-operation": "download",
        "process-file": "yes",
        "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
        subdomain,
        Accept: "application/json",
        "Content-Type": "application/json",
      };

      const response = await httpPost(url, "", {
        responseType: "blob",
        headers,
      });

      if (!response) return;

      const blobUrl = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileKey.split("/").pop());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Document download failed:", err);
      if (setToast) {
        setToast({
          type: "error",
          severity: "error",
          message: "Failed to download document.",
          status: true,
        });
      }
    }
  };

  const handleDownloadAll = async () => {
    for (const file of visibleExistingFiles) {
      await handleDownload(file);
      await new Promise((resolve) => setTimeout(resolve, 600));
    }
  };

  const pushFilesWithValidation = (incomingFiles) => {
    const nextFiles = [];
    const invalidMessages = [];
    const existingKeys = new Set(
      attachedFiles.map(
        (file) => `${file.name}-${file.size}-${file.lastModified || 0}`
      )
    );

    incomingFiles.forEach((file) => {
      const fileName = file.name || "";
      const lowerName = fileName.toLowerCase();
      const isAllowed = ALLOWED_FILE_EXTENSIONS.some((ext) =>
        lowerName.endsWith(ext)
      );

      if (!isAllowed) {
        invalidMessages.push(`${fileName}: unsupported file type`);
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        invalidMessages.push(`${fileName}: exceeds 10 MB`);
        return;
      }

      const fileKey = `${file.name}-${file.size}-${file.lastModified || 0}`;
      if (existingKeys.has(fileKey)) {
        return;
      }

      existingKeys.add(fileKey);
      nextFiles.push(file);
    });

    if (nextFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...nextFiles]);
      clearError("attachments");
    }

    if (invalidMessages.length > 0) {
      setErrors((prev) => ({
        ...prev,
        attachments: invalidMessages.join(", "),
      }));
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => {
      const nextValue = type === "checkbox" ? checked : value;
      const nextState = {
        ...prev,
        [name]: nextValue,
      };

      if (name === "savedStage" && !value) {
        nextState.isMandatory = false;
      }

      return nextState;
    });

    clearError(name);
  };

  const handleApprovalToggle = (event) => {
    const checked = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      taskType: checked ? "ApprovalBased" : "Normal",
      // ponytail: self can't approve own task, so no auto-fill when assigned to self
      approver: checked
        ? prev.approver || (isLoggedInUser(prev.assignedTo) ? "" : prev.assignedTo || "")
        : "",
      status: checked
        ? prev.status === "Closed"
          ? "Open"
          : prev.status
        : prev.status === "Approved" || prev.status === "Rejected"
        ? "Open"
        : prev.status,
    }));

    clearError("approver");
    clearError("status");
  };

  const handleRefTaskChange = (event, newValue) => {
    const nextValue =
      typeof newValue === "string" ? newValue : newValue?.id || "";
    setFormData((prev) => ({
      ...prev,
      referenceTaskId: nextValue,
    }));
    clearError("referenceTaskId");
  };

  const handleContactChange = (fieldName) => (event, newValue) => {
    const email =
      typeof newValue === "string" ? newValue : newValue?.Email || "";
    setFormData((prev) => {
      const next = {
        ...prev,
        [fieldName]: email,
      };

      // Auto-fill approver if it's an approval task and approver is empty or was same as assignedTo
      if (
        fieldName === "assignedTo" &&
        !isEditMode &&
        next.taskType === "ApprovalBased" &&
        (!prev.approver || prev.approver === prev.assignedTo)
      ) {
        next.approver = isLoggedInUser(email) ? "" : email;
      }

      return next;
    });
    clearError(fieldName);
    if (fieldName === "assignedTo") clearError("approver");
  };

  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files || []);
    if (newFiles.length > 0) {
      pushFilesWithValidation(newFiles);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    clearError("attachments");
  };

  const handleRemoveExistingFile = (fileKey) => {
    setRemovedFiles((prev) => [...prev, fileKey]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const droppedFiles = Array.from(event.dataTransfer.files || []);
    if (droppedFiles.length > 0) {
      pushFilesWithValidation(droppedFiles);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.title.trim()) {
      nextErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      nextErrors.description = "Description is required";
    } else if (formData.description.length > 2000) {
      nextErrors.description = "Description must be under 2000 characters";
    }

    if (!loanId && !isEditMode) {
      nextErrors.loanId = "Loan ID is required";
    }

    if (!formData.assignedTo.trim()) {
      nextErrors.assignedTo = "Assigned To is required";
    }

    if (isApprovalTask && !formData.approver.trim()) {
      nextErrors.approver = isLoggedInUser(formData.assignedTo)
        ? "Select a different approver — you cannot approve a task assigned to yourself"
        : "Approver is required for approval tasks";
    }

    if (formData.dueDate) {
      const selectedDate = new Date(formData.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        nextErrors.dueDate = "Due Date cannot be in the past.";
      }
    }

    if (!hasSelectedStage && formData.isMandatory) {
      nextErrors.savedStage = "Select a stage before marking it mandatory";
    }

    setErrors((prev) => ({
      ...prev,
      ...nextErrors,
    }));

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (isTaskClosed) return;
    if (!validate()) return;

    try {
      setSaving(true);
      let response;
      const normalizedStage = formData.savedStage || null;
      const normalizedMandatory = normalizedStage ? formData.isMandatory : false;

      if (isEditMode) {
        let finalStatus = formData.status;

        if (
          !isApprovalTask &&
          editTask.status === "Open" &&
          finalStatus === "Open"
        ) {
          const hasFieldChanges =
            formData.title !== (editTask.title || "") ||
            formData.description !== (editTask.description || "") ||
            formData.notes !== (editTask.notes || "") ||
            (formData.assignedTo || "").toLowerCase() !== (editTask.assignedTo || "").toLowerCase() ||
            (formData.approver || "").toLowerCase() !== (editTask.approver || "").toLowerCase() ||
            formData.dueDate !==
              (editTask.dueDate ? editTask.dueDate.split("T")[0] : "") ||
            normalizedStage !== (editTask.stage || null) ||
            normalizedMandatory !== Boolean(editTask.isMandatory) ||
            formData.referenceTaskId !==
              (editTask.referenceTaskId || editTask.reference?.id || "") ||
            formData.isNotificationRequired !==
              Boolean(editTask.isNotificationRequired);

          if (hasFieldChanges) {
            finalStatus = "InProgress";
          }
        }

        const updatePayload = {
          title: formData.title.trim(),
          description: formData.description.trim() || "",
          status: finalStatus,
          assignedTo: formData.assignedTo.trim() || null,
          approver: isApprovalTask ? formData.approver.trim() || null : null,
          dueDate: formData.dueDate || null,
          loanId,
          stage: normalizedStage,
          isMandatory: normalizedMandatory,
          referenceTaskId: formData.referenceTaskId || null,
          notes: formData.notes.trim() || "",
          isNotificationRequired: formData.isNotificationRequired,
          deletedFiles: removedFiles.length > 0 ? removedFiles : null,
        };

        if (attachedFiles.length > 0) {
          const multipartData = new FormData();
          multipartData.append("taskData", JSON.stringify(updatePayload));
          attachedFiles.forEach((file) => multipartData.append("files", file));
          response = await httpPut(
            `${TASK_API_BASE}/${editTask.id}/multipart/details`,
            multipartData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        } else {
          response = await httpPut(
            `${TASK_API_BASE}/${editTask.id}/details`,
            updatePayload
          );
        }
      } else {
        const payload = {
          taskType: formData.taskType,
          title: formData.title.trim(),
          description: formData.description.trim() || "",
          status: formData.status || "Open",
          assignedTo: formData.assignedTo.trim() || null,
          approver: isApprovalTask ? formData.approver.trim() || null : null,
          dueDate: formData.dueDate || null,
          stage: normalizedStage,
          isMandatory: normalizedMandatory,
          referenceTaskId: formData.referenceTaskId.trim() || null,
          notes: formData.notes.trim() || "",
          isNotificationRequired: formData.isNotificationRequired,
          loanId,
        };

        if (attachedFiles.length > 0) {
          const multipartData = new FormData();
          multipartData.append("taskData", JSON.stringify(payload));
          attachedFiles.forEach((file) => multipartData.append("files", file));
          response = await httpPost(`${TASK_API_BASE}/multipart`, multipartData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await httpPost(TASK_API_BASE, payload);
        }
      }

      if (response?.response?.status >= 400) {
        throw new Error(
          response?.response?.data?.message ||
            `Failed to ${isEditMode ? "update" : "create"} task`
        );
      }

      if (setToast) {
        setToast({
          type: toastSeverity.SUCCESS,
          severity: toastSeverity.SUCCESS,
          message: isEditMode
            ? "Task updated successfully."
            : "Task created successfully.",
          status: true,
        });
      }

      if (props.getCurrentPage) {
        const pageUrl = window.location.pathname.split("/").filter(Boolean)[0];
        props.getCurrentPage({ pageUrl, crmId: loanId });
      }

      if (onTaskCreated) onTaskCreated(response);
      onClose();
    } catch (err) {
      const errorMsg =
        err?.message || `Failed to ${isEditMode ? "update" : "create"} task.`;
      setErrors((prev) => ({ ...prev, submit: errorMsg }));
      if (setToast) {
        setToast({
          type: toastSeverity.ERROR,
          severity: toastSeverity.ERROR,
          message: errorMsg,
          status: true,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className={classes.formContainer}>
      <form onSubmit={handleSubmit} noValidate>
        {isTaskClosed && (
          <Box className={classes.readOnlyBanner}>
            <span>!</span>
            This task is {editTask.status} and cannot be edited.
          </Box>
        )}

        <Typography className={classes.sectionLabel}>Basic Information</Typography>
        <Box className={classes.sectionCard}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Title *"
                name="title"
                size="small"
                value={formData.title}
                onChange={handleChange}
                className={classes.field}
                error={Boolean(errors.title)}
                helperText={errors.title}
                disabled={isTaskClosed}
                InputProps={{}}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                rows={2}
                rowsMax={15}
                variant="outlined"
                label="Description *"
                name="description"
                size="small"
                value={formData.description}
                onChange={handleChange}
                className={classes.field}
                error={Boolean(errors.description)}
                helperText={errors.description}
                disabled={isTaskClosed}
                inputProps={{ maxLength: 2000 }}
                InputProps={{}}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                rows={2}
                rowsMax={15}
                variant="outlined"
                label="Notes"
                name="notes"
                size="small"
                value={formData.notes}
                onChange={handleChange}
                className={classes.field}
                disabled={isTaskClosed}
              />
            </Grid>
          </Grid>
        </Box>


        <Typography className={classes.sectionLabel}>Assignment & Timeline</Typography>
        <Box className={classes.sectionCard}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Status"
                name="status"
                size="small"
                value={formData.status}
                onChange={handleChange}
                className={classes.field}
                disabled={isTaskClosed || !isEditMode}
                InputProps={{}}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  },
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  fullWidth
                  inputVariant="outlined"
                  label="Due Date"
                  format="dd/MM/yyyy"
                  autoOk
                  disablePast
                  size="small"
                  value={formData.dueDate ? new Date(formData.dueDate) : null}
                  onChange={(date) => {
                    if (date && !isNaN(date.getTime())) {
                      const offset = date.getTimezoneOffset();
                      const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
                      const dateString = adjustedDate.toISOString().split("T")[0];
                      setFormData((prev) => ({ ...prev, dueDate: dateString }));
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (adjustedDate < today) {
                        setErrors((prev) => ({ ...prev, dueDate: "Due Date cannot be in the past." }));
                      } else {
                        clearError("dueDate");
                      }
                    } else {
                      setFormData((prev) => ({ ...prev, dueDate: "" }));
                      clearError("dueDate");
                    }
                  }}
                  className={classes.field}
                  error={Boolean(errors.dueDate)}
                  helperText={errors.dueDate}
                  disabled={isTaskClosed}
                  InputLabelProps={{ shrink: true }}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                    style: { padding: 8 },
                  }}
                  keyboardIcon={
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: "1rem", color: "var(--color-text-secondary)" }} />
                  }
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={availableContacts}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  if (typeof option === "string") return option;
                  return option.Name
                    ? `${option.Name} (${option.Email})`
                    : option.Email || "";
                }}
                renderOption={(option) => {
                  const emailKey = (option.Email || "").trim().toLowerCase();
                  const photo = contactPhotos[emailKey];
                  return (
                    <Box className={classes.optionContainer}>
                      <div className={classes.avatar}>
                        {photo ? (
                          <img
                            src={photo}
                            className={classes.avatarImg}
                            alt={option.Name}
                          />
                        ) : (
                          getInitials(option?.Name || option?.Email)
                        )}
                      </div>
                      <Box>
                        <Typography
                          style={{
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            color: "var(--color-text-primary)",
                          }}
                        >
                          {option.Name}
                        </Typography>
                        <Typography style={{ fontSize: "0.6875rem", color: "var(--color-text-secondary)" }}>
                          {option.Email} {option.Role ? `- ${option.Role}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                loading={contactsLoading}
                value={
                  availableContacts.find(
                    (contact) => (contact.Email || "").toLowerCase() === (formData.assignedTo || "").toLowerCase()
                  ) || null
                }
                onChange={handleContactChange("assignedTo")}
                disabled={isTaskClosed}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Assigned To *"
                    size="small"
                    className={classes.field}
                    placeholder="Search user..."
                    error={Boolean(errors.assignedTo)}
                    helperText={errors.assignedTo}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {contactsLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {(!isEditMode || isApprovalTask) && (
          <>
            <Typography className={classes.sectionLabel}>Approval Details</Typography>
            <Box className={classes.sectionCard}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={isApprovalTask ? 4 : 12}>
                  <FormControlLabel
                    className={classes.checkboxRow}
                    control={
                      <Checkbox
                        checked={isApprovalTask}
                        onChange={handleApprovalToggle}
                        color="primary"
                        disabled={isTaskClosed || isEditMode}
                      />
                    }
                    label="Requires Approval"
                  />
                </Grid>
                {isApprovalTask && (
                  <Grid item xs={12} sm={8}>
                    <Autocomplete
                      options={availableContacts.filter(
                        (contact) => !isLoggedInUser(contact.Email)
                      )}
                      getOptionLabel={(option) => {
                        if (!option) return "";
                        if (typeof option === "string") return option;
                        return option.Name
                          ? `${option.Name} (${option.Email})`
                          : option.Email || "";
                      }}
                      renderOption={(option) => {
                        const emailKey = (option.Email || "")
                          .trim()
                          .toLowerCase();
                        const photo = contactPhotos[emailKey];
                        return (
                          <Box className={classes.optionContainer}>
                            <div className={classes.avatar}>
                              {photo ? (
                                <img
                                  src={photo}
                                  className={classes.avatarImg}
                                  alt={option.Name}
                                />
                              ) : (
                                getInitials(option.Name || option.Email)
                              )}
                            </div>
                            <Box>
                              <Typography
                                style={{
                                  fontSize: "0.8125rem",
                                  fontWeight: 600,
                                  color: "var(--color-text-primary)",
                                }}
                              >
                                {option.Name}
                              </Typography>
                              <Typography
                                style={{ fontSize: "0.6875rem", color: "var(--color-text-secondary)" }}
                              >
                                {option.Email}{" "}
                                {option.Role ? `- ${option.Role}` : ""}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                      loading={contactsLoading}
                      value={
                        availableContacts.find(
                          (contact) => (contact.Email || "").toLowerCase() === (formData.approver || "").toLowerCase()
                        ) || null
                      }
                      onChange={handleContactChange("approver")}
                      disabled={isTaskClosed || (isEditMode && !isRequestor)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          label="Approver *"
                          size="small"
                          className={classes.field}
                          placeholder="Search approver..."
                          error={Boolean(errors.approver)}
                          helperText={errors.approver}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {contactsLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </>
        )}

        <Typography className={classes.sectionLabel}>Workflow Details</Typography>
        <Box className={classes.sectionCard}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Loan Stage"
                name="savedStage"
                size="small"
                value={formData.savedStage}
                onChange={handleChange}
                className={classes.field}
                disabled={isTaskClosed || stagesLoading}
                error={Boolean(errors.savedStage)}
                helperText={errors.savedStage}
                InputProps={{}}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  },
                }}
              >
                <MenuItem value="">None</MenuItem>
                {Object.entries(stageGroups).map(([groupName, stages]) => [
                  <ListSubheader
                    key={`group-${groupName}`}
                    disableSticky
                    className={classes.groupLabel}
                  >
                    {groupName}
                  </ListSubheader>,
                  ...(Array.isArray(stages)
                    ? stages.map((stage) => (
                        <MenuItem key={`${groupName}-${stage}`} value={stage}>
                          {stage}
                        </MenuItem>
                      ))
                    : []),
                ])}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={referenceTasks}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  if (typeof option === "string") return option;
                  const hasTitle =
                    option.title &&
                    option.title !== "null" &&
                    option.title.trim() !== "";
                  if (option.taskNumber) {
                    return hasTitle
                      ? `#${option.taskNumber} - ${option.title}`
                      : `#${option.taskNumber}`;
                  }
                  return hasTitle ? option.title : "";
                }}
                renderOption={(option) => (
                  <Box>
                    <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Typography
                        style={{
                          fontSize: "0.625rem",
                          fontWeight: 700,
                          color: "var(--color-primary-text)",
                          backgroundColor: "var(--total_row_background)",
                          padding: "2px 6px",
                          borderRadius: "calc(4px * var(--app-radius-scale, 1))",
                          border: "1px solid var(--total_row_boldbackground)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        #{option.taskNumber}
                      </Typography>
                      {option.title &&
                        option.title !== "null" &&
                        option.title.trim() !== "" && (
                          <Typography
                            style={{
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              color: "var(--color-text-primary)",
                            }}
                          >
                            {option.title}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                )}
                loading={refTasksLoading}
                value={
                  referenceTasks.find(
                    (task) => task.id === formData.referenceTaskId
                  ) || null
                }
                onChange={handleRefTaskChange}
                disabled={isTaskClosed}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Reference Task"
                    size="small"
                    className={classes.field}
                    placeholder="Search task..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {refTasksLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* <Typography className={classes.sectionLabel}>File Upload</Typography> */}
        <Box className={classes.sectionCard} style={{marginTop:"10px"}}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_FILE_EXTENSIONS.join(",")}
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <Grid container spacing={2}>
            {!isTaskClosed ? (
              <>
                <Grid item xs={12} sm={6}>
                  <Box
                    className={`${classes.dropzone} ${
                      isDragOver ? classes.dropzoneActive : ""
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{ minHeight: 80, height: "100%" }}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon
                      icon={faCloudUploadAlt}
                      className={classes.dropzoneIcon}
                      style={{ fontSize: "1.5rem", marginBottom: 4 }}
                    />
                    <Typography className={classes.dropzoneText}>
                      Drop files or <span className={classes.dropzoneBrowse}>browse</span>
                    </Typography>
                    <Typography className={classes.uploadHint} style={{ textAlign: "center" }}>
                      Supports most document types. Max 10 MB per file.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {visibleExistingFiles.length > 0 && (
                    <Box className={classes.fileList}>
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0 4px 4px",
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: "0.625rem",
                            color: "#64748b",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.025em",
                          }}
                        >
                          Existing ({visibleExistingFiles.length})
                        </Typography>
                        {visibleExistingFiles.length > 1 && (
                          <Typography
                            onClick={handleDownloadAll}
                            style={{
                              fontSize: "0.625rem",
                              color: "var(--color-primary-text)",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                            tabIndex={-1}
                          >
                            Download All
                          </Typography>
                        )}
                      </Box>
                      {visibleExistingFiles.map((file, index) => {
                        const fileName = file.includes("/") ? file.split("/").pop() : file;
                        return (
                          <Box
                            key={`existing-${index}`}
                            className={classes.fileItem}
                            style={{ background: "var(--total_row_background)", borderColor: "var(--total_row_boldbackground)" }}
                          >
                            {getFileIcon(fileName)}
                            <Typography className={classes.fileName} style={{ fontSize: "0.6875rem" }}>
                              {fileName}
                            </Typography>
                            <Box style={{ display: "flex", gap: 2 }}>
                              <Tooltip title="Download" arrow>
                                <IconButton
                                  tabIndex={-1}
                                  onClick={() => handleDownload(file)}
                                  size="small"
                                  style={{ padding: 2, color: "var(--color-primary-text)" }}
                                >
                                  <FontAwesomeIcon
                                    icon={faFileDownload}
                                    style={{ fontSize: "0.75rem" }}
                                  />
                                </IconButton>
                              </Tooltip>
                              {!isTaskClosed && (
                                <Tooltip title="Remove" arrow>
                                  <IconButton
                                    tabIndex={-1}
                                    onClick={() => handleRemoveExistingFile(file)}
                                    size="small"
                                    className={classes.fileRemoveBtn}
                                  >
                                    <CloseIcon style={{ fontSize: "0.875rem" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {attachedFiles.length > 0 && (
                    <Box
                      className={classes.fileList}
                      style={{ marginTop: visibleExistingFiles.length > 0 ? 8 : 0 }}
                    >
                      <Typography
                        style={{
                          fontSize: "0.625rem",
                          color: "var(--color-text-secondary)",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          padding: "0 4px 4px",
                        }}
                      >
                        New Uploads ({attachedFiles.length})
                      </Typography>
                      {attachedFiles.map((file, index) => (
                        <Box key={`${file.name}-${index}`} className={classes.fileItem}>
                          {getFileIcon(file.name)}
                          <Typography className={classes.fileName} style={{ fontSize: "0.6875rem" }}>
                            {file.name}
                          </Typography>
                          <Typography className={classes.fileSize}>
                            {formatFileSize(file.size)}
                          </Typography>
                          <Tooltip title="Remove" arrow>
                            <IconButton
                              tabIndex={-1}
                              onClick={() => handleRemoveFile(index)}
                              size="small"
                              className={classes.fileRemoveBtn}
                            >
                              <CloseIcon style={{ fontSize: "0.875rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {visibleExistingFiles.length === 0 && attachedFiles.length === 0 && (
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        minHeight: 80,
                        background: "var(--color-background-default)",
                        borderRadius: "calc(10px * var(--app-radius-scale, 1))",
                        border: "1px dashed var(--total_row_boldbackground)",
                      }}
                    >
                      <Typography style={{ fontSize: "0.6875rem", color: "var(--color-text-secondary)" }}>
                        No files attached
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                {visibleExistingFiles.length > 0 && (
                  <Box className={classes.fileList}>
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0 4px 4px",
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: "0.625rem",
                          color: "var(--color-text-secondary)",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                        }}
                      >
                        Existing ({visibleExistingFiles.length})
                      </Typography>
                      {visibleExistingFiles.length > 1 && (
                        <Typography
                          onClick={handleDownloadAll}
                          style={{
                            fontSize: "0.625rem",
                            color: "var(--color-primary-text)",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                          tabIndex={-1}
                        >
                          Download All
                        </Typography>
                      )}
                    </Box>
                    {visibleExistingFiles.map((file, index) => {
                      const fileName = file.includes("/") ? file.split("/").pop() : file;
                      return (
                        <Box
                          key={`existing-${index}`}
                          className={classes.fileItem}
                          style={{ background: "var(--total_row_background)", borderColor: "var(--total_row_boldbackground)" }}
                        >
                          {getFileIcon(fileName)}
                          <Typography className={classes.fileName} style={{ fontSize: "0.6875rem" }}>
                            {fileName}
                          </Typography>
                          <Box style={{ display: "flex", gap: 2 }}>
                            <Tooltip title="Download" arrow>
                              <IconButton
                                tabIndex={-1}
                                onClick={() => handleDownload(file)}
                                size="small"
                                style={{ padding: 2, color: "var(--color-primary-text)" }}
                              >
                                <FontAwesomeIcon
                                  icon={faFileDownload}
                                  style={{ fontSize: "0.75rem" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {visibleExistingFiles.length === 0 && (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      minHeight: 60,
                      background: "var(--color-background-default)",
                      borderRadius: "calc(10px * var(--app-radius-scale, 1))",
                      border: "1px dashed var(--total_row_boldbackground)",
                    }}
                  >
                    <Typography style={{ fontSize: "0.6875rem", color: "var(--color-text-secondary)" }}>
                      No files attached
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>

          {errors.attachments && (
            <Typography className={classes.errorText}>
              {errors.attachments}
            </Typography>
          )}
        </Box>

        {errors.submit && (
          <Typography className={classes.errorText}>{errors.submit}</Typography>
        )}
        {errors.loanId && (
          <Typography className={classes.errorText}>{errors.loanId}</Typography>
        )}

        {mounted &&
          document.getElementById("dialog-actions-container") &&
          createPortal(
            <Box className={classes.dialogFooter}>
              <Box className={classes.dialogFooterChecks}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isNotificationRequired"
                      checked={formData.isNotificationRequired}
                      onChange={handleChange}
                      color="primary"
                      disabled={isTaskClosed}
                      size="small"
                      style={{ padding: 4 }}
                    />
                  }
                  label={
                    <Typography
                      style={{
                        fontSize: "0.8125rem",
                        color: "var(--color-text-secondary)",
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      Enable Notifications
                    </Typography>
                  }
                  style={{ margin: 0, display: "flex", alignItems: "center" }}
                />
                {hasSelectedStage && (
                  <Tooltip
                    title={
                      isEditMode && !isTaskAdmin && !isTaskClosed
                        ? "Only Task Administrators can modify this setting"
                        : ""
                    }
                    arrow
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isMandatory"
                          checked={Boolean(formData.isMandatory)}
                          onChange={handleChange}
                          color="primary"
                          disabled={isTaskClosed || (isEditMode && !isTaskAdmin)}
                          size="small"
                          style={{ padding: 4 }}
                        />
                      }
                      label={
                        <Typography
                          style={{
                            fontSize: "0.8125rem",
                            color: "var(--color-text-secondary)",
                            fontWeight: 600,
                            lineHeight: 1,
                          }}
                        >
                          Mandatory for stage progression
                        </Typography>
                      }
                      style={{ margin: 0, display: "flex", alignItems: "center" }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Box className={classes.dialogFooterButtons}>
                <A_Button
                  label="Cancel"
                  premiumVariant="secondary"
                  className={classes.cancelBtn}
                  onClick={onClose}
                  disabled={saving}
                />
                {!isTaskClosed && (
                  <A_SecuredAction permission={pageSecurityActions.UPDATE}>
                    <A_Button
                      type="submit"
                      label={
                        saving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : isEditMode ? (
                          "Save Changes"
                        ) : (
                          "Create Task"
                        )
                      }
                      premiumVariant="primary"
                      className={classes.submitBtn}
                      onClick={handleSubmit}
                      disabled={saving}
                    />
                  </A_SecuredAction>
                )}
              </Box>
            </Box>,
            document.getElementById("dialog-actions-container")
          )}
      </form>
    </Box>
  );
};

M_AddTaskForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onTaskCreated: PropTypes.func,
  loanId: PropTypes.string,
  setToast: PropTypes.func,
  getCurrentPage: PropTypes.func,
  editTask: PropTypes.object,
};

const mapStateToProps = (state) => ({
  toast: state.toast,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...toastActions, ...currentPageActions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(M_AddTaskForm);
